import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { api } from '../utils/api';
import { 
  LuDollarSign, LuTrendingUp, LuTrendingDown, LuChartPie, 
  LuServer, LuCloud, LuDatabase, LuHardDrive, LuActivity,
  LuCircleAlert, LuCircleCheck
} from 'react-icons/lu';
import { 
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, 
  Tooltip, ResponsiveContainer, AreaChart, Area 
} from 'recharts';
import { summarizeResourceCosts } from '../utils/costCoverage';

const COLORS = ['#f59e0b', '#14b8a6', '#3b82f6', '#8b5cf6', '#ef4444', '#22c55e'];

// Custom Tooltip
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 shadow-xl">
        <p className="text-xs text-zinc-400">{payload[0].name || payload[0].payload?.name}</p>
        <p className="text-sm font-semibold text-zinc-100">${payload[0].value?.toFixed(2)}</p>
      </div>
    );
  }
  return null;
};

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'amber' }) => {
  const colorClasses = {
    amber: 'text-amber-400 bg-amber-500/10',
    teal: 'text-teal-400 bg-teal-500/10',
    blue: 'text-blue-400 bg-blue-500/10'
  };

  return (
    <div className="glass-card rounded-xl p-5">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs ${trend >= 0 ? 'text-amber-400' : 'text-teal-400'}`}>
            {trend >= 0 ? <LuTrendingUp className="w-3 h-3" /> : <LuTrendingDown className="w-3 h-3" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="text-xs text-zinc-500 uppercase tracking-wider">{title}</p>
      <p className="text-2xl font-semibold text-zinc-100 mt-1">{value}</p>
      {subtitle && <p className="text-xs text-zinc-600 mt-1">{subtitle}</p>}
    </div>
  );
};

// Cost Breakdown by Type
const CostByTypeChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="glass-card rounded-xl p-5 h-full">
        <h3 className="text-sm font-medium text-zinc-300 mb-4">Cost by Resource Type</h3>
        <div className="flex items-center justify-center h-48 text-zinc-600 text-sm">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-xl p-5">
      <h3 className="text-sm font-medium text-zinc-300 mb-4">Cost by Resource Type</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
          <BarChart data={data} layout="vertical" margin={{ left: 0, right: 20 }}>
            <XAxis type="number" tickFormatter={(v) => `$${v}`} tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" tick={{ fill: '#a1a1aa', fontSize: 11 }} axisLine={false} tickLine={false} width={80} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.02)' }} />
            <Bar dataKey="cost" radius={[0, 4, 4, 0]}>
              {data.map((_, index) => (
                <Cell key={index} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Cost by Provider Pie
const CostByProviderChart = ({ data }) => {
  const total = useMemo(() => data.reduce((sum, d) => sum + d.value, 0), [data]);

  if (!data || data.length === 0) {
    return (
      <div className="glass-card rounded-xl p-5 h-full">
        <h3 className="text-sm font-medium text-zinc-300 mb-4">Cost by Provider</h3>
        <div className="flex items-center justify-center h-48 text-zinc-600 text-sm">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-xl p-5">
      <h3 className="text-sm font-medium text-zinc-300 mb-4">Cost by Provider</h3>
      <div className="flex flex-col sm:flex-row sm:items-center gap-6 min-w-0">
        <div className="w-40 h-40 shrink-0 self-center">
          <ResponsiveContainer width="100%" height="100%" minWidth={160} minHeight={160}>
            <PieChart>
              <Pie
                data={data}
                innerRadius={45}
                outerRadius={65}
                paddingAngle={2}
                dataKey="value"
              >
                {data.map((_, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex-1 space-y-2">
          {data.map((item, index) => (
            <div key={item.name} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: COLORS[index % COLORS.length] }} 
                />
                <span className="text-sm text-zinc-400">{item.name}</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-mono text-zinc-200">${item.value.toFixed(0)}</span>
                <span className="text-xs text-zinc-600 ml-2">
                  {total > 0 ? Math.round((item.value / total) * 100) : 0}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Resource Distribution
const ResourceDistribution = ({ analytics }) => {
  const typeIcons = {
    vm: LuServer,
    storage: LuHardDrive,
    database: LuDatabase,
    network: LuCloud,
    load_balancer: LuActivity
  };

  const distribution = useMemo(() => {
    if (!analytics?.resource_count_by_type) return [];
    return Object.entries(analytics.resource_count_by_type).map(([type, count]) => ({
      type,
      count,
      icon: typeIcons[type] || LuCloud
    }));
  }, [analytics]);

  return (
    <div className="glass-card rounded-xl p-5">
      <h3 className="text-sm font-medium text-zinc-300 mb-4">Resource Distribution</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {distribution.map(({ type, count, icon: Icon }) => (
          <div key={type} className="flex items-center gap-3 p-3 bg-zinc-800/30 rounded-lg">
            <div className="w-8 h-8 rounded-md bg-zinc-800 flex items-center justify-center">
              <Icon className="w-4 h-4 text-zinc-400" />
            </div>
            <div>
              <p className="text-lg font-semibold text-zinc-200">{count}</p>
              <p className="text-xs text-zinc-500 capitalize">{type.replace('_', ' ')}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Cost Insights
const CostInsights = ({ analytics }) => {
  const insights = useMemo(() => {
    if (!analytics) return [];
    
    const list = [];
    const total = analytics.total_cost || 0;
    
    // Find highest cost type
    if (analytics.cost_by_type) {
      const sorted = Object.entries(analytics.cost_by_type).sort((a, b) => b[1] - a[1]);
      if (sorted.length > 0) {
        const [topType, topCost] = sorted[0];
        const percent = total > 0 ? Math.round((topCost / total) * 100) : 0;
        list.push({
          text: `${topType.replace('_', ' ')} accounts for ${percent}% of your spend`,
          type: percent > 70 ? 'warning' : 'info'
        });
      }
    }
    
    // Resource efficiency
    const running = analytics.running_resources || 0;
    const stopped = analytics.stopped_resources || 0;
    const uncertain = (analytics.unknown_resources || 0) + (analytics.degraded_resources || 0);
    const transitioning = analytics.transitioning_resources || 0;
    if (stopped > 0) {
      list.push({
        text: `${stopped} stopped resource${stopped > 1 ? 's' : ''} need an owner and workload review`,
        type: 'warning'
      });
    }
    
    if (uncertain > 0) {
      list.push({
        text: `${uncertain} resource state${uncertain === 1 ? '' : 's'} need provider verification`,
        type: 'warning'
      });
    }

    if (running > 0 && stopped === 0 && uncertain === 0 && transitioning === 0) {
      list.push({
        text: 'All resources are active and running',
        type: 'success'
      });
    }
    
    return list.slice(0, 3);
  }, [analytics]);

  const typeColors = {
    warning: 'text-amber-400 bg-amber-500/10',
    success: 'text-teal-400 bg-teal-500/10',
    info: 'text-blue-400 bg-blue-500/10'
  };

  return (
    <div className="glass-card rounded-xl p-5">
      <h3 className="text-sm font-medium text-zinc-300 mb-4">Cost Insights</h3>
      <div className="space-y-3">
        {insights.map((insight, index) => (
          <div key={index} className={`p-3 rounded-lg ${typeColors[insight.type]}`}>
            <p className="text-sm">{insight.text}</p>
          </div>
        ))}
        {insights.length === 0 && (
          <p className="text-sm text-zinc-600">No insights available yet</p>
        )}
      </div>
    </div>
  );
};

// Main Component
// Intelligence Statement Component
const IntelligenceStatement = ({ analytics }) => {
  const generateInsight = () => {
    const totalCost = analytics?.total_cost || 0;
    const pricedCount = analytics?.cost_accuracy?.priced_count || 0;
    const costByType = analytics?.cost_by_type || {};
    const costByProvider = analytics?.cost_by_provider || {};
    const running = analytics?.running_resources || 0;
    const stopped = analytics?.stopped_resources || 0;
    const uncertain = (analytics?.unknown_resources || 0) + (analytics?.degraded_resources || 0);
    const transitioning = analytics?.transitioning_resources || 0;
    
    // Find primary cost driver
    const sortedTypes = Object.entries(costByType).sort((a, b) => b[1] - a[1]);
    const topType = sortedTypes[0];
    
    const sortedProviders = Object.entries(costByProvider).sort((a, b) => b[1] - a[1]);
    const topProvider = sortedProviders[0];
    
    // Generate contextual narrative
    if (!analytics?.total_resources) {
      return {
        type: 'neutral',
        primary: 'Connect your first provider to begin analysis',
        secondary: 'Analytics will update automatically as you discover resources',
        icon: LuCloud
      };
    }

    if (pricedCount === 0) {
      return {
        type: 'neutral',
        primary: 'Monthly cost is unknown',
        secondary: 'Connected resources do not yet have usable provider or manual pricing evidence',
        icon: LuDollarSign
      };
    }
    
    if (uncertain > 0) {
      return {
        type: 'alert',
        primary: `${uncertain} resource state${uncertain === 1 ? '' : 's'} need verification`,
        secondary: 'Cost analysis remains available, but operational state is not fully current',
        icon: LuCircleAlert
      };
    }

    if (transitioning > 0) {
      return {
        type: 'warning',
        primary: `${transitioning} provider operation${transitioning === 1 ? '' : 's'} in progress`,
        secondary: 'Operational totals will settle after reconciliation',
        icon: LuActivity
      };
    }

    // Cost pressure narrative
    if (topType && topType[1] / totalCost > 0.65) {
      return {
        type: 'warning',
        primary: `${topType[0].replace('_', ' ')} is the largest known spend category`,
        secondary: `${Math.round((topType[1] / totalCost) * 100)}% of the recorded $${totalCost.toFixed(0)}/month`,
        icon: LuTrendingUp
      };
    }
    
    // Efficiency narrative
    if (stopped > 0) {
      return {
        type: 'warning',
        primary: `${stopped} stopped resource${stopped > 1 ? 's' : ''} need review`,
        secondary: `Confirm purpose, ownership, and provider billing before changing them. ${running} resource${running === 1 ? ' is' : 's are'} currently running`,
        icon: LuCircleAlert
      };
    }
    
    // Healthy narrative
    return {
      type: 'positive',
      primary: `Observed states are current across ${Object.keys(costByType).length} resource types`,
      secondary: `${running} running resource${running === 1 ? '' : 's'} · $${totalCost.toFixed(0)}/month recorded across ${Object.keys(costByProvider).length} provider${Object.keys(costByProvider).length !== 1 ? 's' : ''}`,
      icon: LuCircleCheck
    };
  };

  const insight = useMemo(() => generateInsight(), [analytics]);
  
  const typeStyles = {
    positive: 'text-teal-400',
    warning: 'text-amber-400',
    alert: 'text-rose-400',
    neutral: 'text-zinc-400'
  };

  const Icon = insight.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-8"
    >
      <div className="flex items-start gap-4">
        <div className="mt-1">
          <Icon className={`w-5 h-5 ${typeStyles[insight.type]}`} />
        </div>
        <div>
          <h1 className={`text-2xl md:text-3xl font-semibold tracking-tight ${typeStyles[insight.type]}`}>
            {insight.primary}
          </h1>
          <p className="text-base text-zinc-500 mt-1">
            {insight.secondary}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Cost Trend Component (With real historical data)
const CostTrendCard = ({ analytics, costHistory }) => {
  const total = analytics?.total_cost || 0;
  const pricedCount = analytics?.cost_accuracy?.priced_count || 0;
  
  // Calculate trend from historical data
  const { trend, trendPercent, sparklineData } = useMemo(() => {
    if (!costHistory || costHistory.length === 0) {
      return {
        trend: 'stable',
        trendPercent: 0,
        sparklineData: []
      };
    }
    
    // Sort by date
    const sorted = [...costHistory].sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Calculate trend (compare last value to average of previous values)
    if (sorted.length >= 2) {
      const lastCost = sorted[sorted.length - 1].cost;
      const previousAvg = sorted.slice(0, -1).reduce((sum, d) => sum + d.cost, 0) / (sorted.length - 1);
      const percentChange = previousAvg > 0 ? ((lastCost - previousAvg) / previousAvg) * 100 : 0;
      
      return {
        trend: percentChange >= 0 ? 'up' : 'down',
        trendPercent: Math.abs(Math.round(percentChange)),
        sparklineData: sorted.slice(-14).map(d => ({ value: d.cost })) // Last 14 days
      };
    }
    
    // Single data point - no trend
    return {
      trend: 'stable',
      trendPercent: 0,
      sparklineData: sorted.map(d => ({ value: d.cost }))
    };
  }, [costHistory, total]);

  return (
    <div className="glass-card rounded-xl p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Monthly Spend</p>
          <div className="flex items-end gap-2">
            <span className="text-4xl font-semibold tracking-tight text-zinc-100">
              {pricedCount ? `$${total.toFixed(0)}` : '—'}
            </span>
            {trend !== 'stable' && (
              <div className={`flex items-center gap-1 text-sm pb-1 ${
                trend === 'up' ? 'text-amber-400' : 'text-teal-400'
              }`}>
                {trend === 'up' ? <LuTrendingUp className="w-4 h-4" /> : <LuTrendingDown className="w-4 h-4" />}
                {trendPercent}% this week
              </div>
            )}
          </div>
        </div>
        
        {/* Sparkline */}
        {sparklineData.length > 0 && (
          <div className="w-24 h-12 opacity-60">
            <ResponsiveContainer width="100%" height="100%" minWidth={96} minHeight={48}>
              <AreaChart data={sparklineData}>
                <defs>
                  <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={trend === 'up' ? '#f59e0b' : '#14b8a6'} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={trend === 'up' ? '#f59e0b' : '#14b8a6'} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area 
                  type="monotone" 
                  dataKey="value" 
                  stroke={trend === 'up' ? '#f59e0b' : '#14b8a6'}
                  strokeWidth={1.5}
                  fill="url(#trendGradient)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      
      {/* Contextual explanation */}
      <p className="text-sm text-zinc-500">
        {!pricedCount ? (
          'Monthly cost is unknown because no resource has usable pricing evidence.'
        ) : !costHistory || costHistory.length === 0 ? (
          'Historical cost data will appear here once available from your providers.'
        ) : trend === 'up' ? (
          'Recorded costs are trending up. The available data does not establish the cause.'
        ) : trend === 'down' ? (
          'Recorded costs are trending down. Confirm provider coverage before attributing the change.'
        ) : (
          'Cost is stable. Continue monitoring for any changes.'
        )}
      </p>
    </div>
  );
};

// Primary Driver Card (Top insight)
const PrimaryDriverCard = ({ analytics }) => {
  const costByType = analytics?.cost_by_type || {};
  const costByProvider = analytics?.cost_by_provider || {};
  const total = analytics?.total_cost || 0;
  
  const topType = Object.entries(costByType).sort((a, b) => b[1] - a[1])[0];
  const topProvider = Object.entries(costByProvider).sort((a, b) => b[1] - a[1])[0];

  return (
    <div className="glass-card rounded-xl p-6">
      <p className="text-xs text-zinc-500 uppercase tracking-wider mb-4">Primary Spend Driver</p>
      
      <div className="space-y-5">
        {total > 0 && topType && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-zinc-300">
                {topType[0].replace('_', ' ')}
              </p>
              <p className="text-sm font-semibold text-zinc-100">
                ${topType[1].toFixed(0)}
              </p>
            </div>
            <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-amber-500 to-amber-400"
                style={{ width: `${(topType[1] / total) * 100}%` }}
              />
            </div>
            <p className="text-xs text-zinc-500 mt-2">
              {Math.round((topType[1] / total) * 100)}% of total spend
            </p>
          </div>
        )}
        
        {total > 0 && topProvider && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-zinc-300">
                {topProvider[0]}
              </p>
              <p className="text-sm font-semibold text-zinc-100">
                ${topProvider[1].toFixed(0)}
              </p>
            </div>
            <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-blue-400"
                style={{ width: `${(topProvider[1] / total) * 100}%` }}
              />
            </div>
            <p className="text-xs text-zinc-500 mt-2">
              Leading provider by spend
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const Analytics = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [summaryError, setSummaryError] = useState(false);
  const [costHistory, setCostHistory] = useState([]);
  const [capabilityWarnings, setCapabilityWarnings] = useState([]);
  const [historyStatus, setHistoryStatus] = useState('idle');
  const [costCoverage, setCostCoverage] = useState({ status: 'loading', known: 0, total: 0, estimated: 0 });

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await api.getAnalyticsSummary();
      setAnalytics(response.data);
      setSummaryError(false);
      setCostCoverage({ status: 'loading', known: 0, total: 0, estimated: 0 });
      api.getResources().then((resourcesResponse) => {
        const resources = Array.isArray(resourcesResponse.data)
          ? resourcesResponse.data
          : (resourcesResponse.data?.resources || []);
        const summary = summarizeResourceCosts(resources);
        setCostCoverage({
          status: 'ready',
          known: summary.pricedCount,
          total: resources.length,
          estimated: summary.priced.filter(({ resource }) => resource.cost_source === 'estimated').length,
        });
      }).catch(() => setCostCoverage({ status: 'error', known: 0, total: 0, estimated: 0 }));
      // The local summary is the page's critical path. Provider history loads
      // independently and may resolve as partial/unavailable.
      setLoading(false);
      
      // Fetch cost history for the last 30 days
      setHistoryStatus('loading');
      setCostHistory([]);
      setCapabilityWarnings([]);
      const endDate = new Date().toISOString();
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      
      try {
        const historyResponse = await api.getCostHistory(startDate, endDate, 'DAILY');
        const historyData = historyResponse.data;
        
        // Store historical data
        if (historyData.data && historyData.data.length > 0) {
          setCostHistory(historyData.data);
        }
        
        // Check for capability warnings
        const warnings = historyData.providers
          ?.filter(p => p.status === 'unsupported' || p.status === 'error')
          .map(p => ({
            provider: p.provider_name,
            type: p.provider_type,
            message: p.message
          })) || [];
        
        if (warnings.length > 0) {
          setCapabilityWarnings(warnings);
        }
        setHistoryStatus(warnings.length > 0 ? 'partial' : 'success');
      } catch (historyError) {
        setHistoryStatus('error');
        console.warn('Cost history not available:', historyError);
        // Don't fail the whole page if history is unavailable
      }
    } catch (error) {
      setSummaryError(true);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const costByProviderData = useMemo(() => {
    if (!analytics?.cost_by_provider || !analytics?.cost_accuracy?.priced_count) return [];
    return Object.entries(analytics.cost_by_provider).map(([name, value]) => ({
      name,
      value: parseFloat(value.toFixed(2)),
    }));
  }, [analytics]);

  const costByTypeData = useMemo(() => {
    if (!analytics?.cost_by_type || !analytics?.cost_accuracy?.priced_count) return [];
    return Object.entries(analytics.cost_by_type).map(([name, value]) => ({
      name: name.replace('_', ' '),
      cost: parseFloat(value.toFixed(2)),
    }));
  }, [analytics]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="w-10 h-10 border-2 border-zinc-700 border-t-amber-400 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-zinc-500 text-sm">Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (summaryError) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <Navbar user={user} onLogout={onLogout} />
        <div role="alert" className="max-w-xl mx-auto px-4 py-20 text-center">
          <LuCircleAlert className="w-8 h-8 text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-zinc-100">Analytics summary is unavailable</h1>
          <p className="text-sm text-zinc-500 mt-2 mb-6">Your infrastructure has not been treated as empty. Try the request again.</p>
          <button onClick={() => { setLoading(true); loadAnalytics(); }} className="px-4 py-2 rounded-lg bg-amber-500 text-zinc-950 font-medium">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
        {/* Intelligence Header */}
        <IntelligenceStatement analytics={analytics} />
        {costCoverage.status === 'loading' && <p role="status" className="mb-6 text-sm text-zinc-500">Checking cost coverage…</p>}
        {costCoverage.status === 'error' && <p role="alert" className="mb-6 rounded-lg border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-200">Cost coverage could not be verified. Treat every amount on this page as incomplete.</p>}
        {costCoverage.status === 'ready' && costCoverage.known < costCoverage.total && <p className="mb-6 rounded-lg border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-100">Known-cost coverage: {costCoverage.known} of {costCoverage.total} resources{costCoverage.estimated ? ` (${costCoverage.estimated} estimated)` : ''}. Totals and percentages exclude resources without usable pricing.</p>}
        {historyStatus === 'loading' && <div role="status" className="mb-6 text-sm text-zinc-500">Loading provider cost history…</div>}
        {historyStatus === 'error' && (
          <div role="alert" className="mb-6 rounded-lg border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-200">
            Cost history is unavailable; the local summary above is still current.
            <button onClick={loadAnalytics} className="ml-3 underline">Retry</button>
          </div>
        )}
        
        {/* Capability Warnings */}
        {capabilityWarnings.length > 0 && (
          <div className="mb-6 space-y-2">
            {capabilityWarnings.map((warning, idx) => (
              <div key={idx} className="glass-card rounded-lg p-4 border-l-4 border-amber-500">
                <div className="flex items-start gap-3">
                  <LuCircleAlert className="w-5 h-5 text-amber-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-zinc-200">
                      {warning.provider} ({warning.type})
                    </p>
                    <p className="text-sm text-zinc-400 mt-1">
                      {warning.message}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Primary Visualization - Cost Trend */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2">
            <CostTrendCard analytics={analytics} costHistory={costHistory} />
          </div>
          <PrimaryDriverCard analytics={analytics} />
        </div>

        {/* Detailed Breakdown - Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <CostByTypeChart data={costByTypeData} />
          <CostByProviderChart data={costByProviderData} />
        </div>

        {/* Summary & Insights */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ResourceDistribution analytics={analytics} />
          <CostInsights analytics={analytics} />
        </div>
      </div>
    </div>
  );
};

export default Analytics;
